/**
 * usePatientNameIndex — Lightweight session-cached index of accessible patient
 * names (owned + active shared) for the Activity Bar patient quick search.
 *
 * It derives its minimal index from the same session-scoped directory used by
 * Today and Patients, so opening search does not issue another patient query.
 *
 * See: docs/features/infrastructure/patient-api.md ("Patient quick search in AI search bar")
 */
import { useMemo } from "react";
import { getPatientNameMatchScore, normalizePatientNameText } from "../utils/patientNameMatching";
import { invalidatePatientDirectory } from "../utils/patientDirectoryService";
import { usePatientDirectory } from "./usePatientDirectory";

/** Drop the cached index and refetch in any mounted consumers. */
export function invalidatePatientNameIndex(userId = null) {
    invalidatePatientDirectory(userId);
}

const toEntry = (patient, isShared) => ({
    id: patient.id,
    firstName: patient.firstName || "",
    lastName: patient.lastName || "",
    isShared,
});

/**
 * @param {Object} options
 * @param {string} options.userId - Current user's uid
 * @param {boolean} options.enabled - Gate (aiAssistant flag); no fetch when false
 * @returns {{ patients: Array, isLoading: boolean }}
 */
export function usePatientNameIndex({ userId, enabled }) {
    const { patients, isLoading } = usePatientDirectory(enabled ? userId : null);
    const index = useMemo(
        () => patients.map((patient) => toEntry(patient, !!patient.isShared)),
        [patients]
    );
    return { patients: index, isLoading: enabled && isLoading };
}

/**
 * Match patients against a query (case-insensitive) on first, last, and full name.
 * Returns up to `limit` results, prefix matches ranked before mid-string matches.
 */
export function matchPatients(patients, query, limit = 8) {
    const q = normalizePatientNameText(query);
    if (q.length < 3) return [];

    const scored = [];
    for (const p of patients) {
        const score = getPatientNameMatchScore(p, q);
        if (score >= 0) scored.push({ patient: p, score });
    }

    return scored
        .sort((a, b) => a.score - b.score || a.patient.lastName.localeCompare(b.patient.lastName))
        .slice(0, limit)
        .map((s) => s.patient);
}
